import '@mui/x-date-pickers/themeAugmentation';
import { alpha, createTheme, darken, lighten } from '@mui/material/styles';
import {
  breakpoints,
  colors,
  radii,
  shadows,
  spacing,
  typography,
  sizes,
  zIndex,
  opacity,
  motion,
  containers,
  density,
  type DensityMode,
  iconSizes,
} from './tokens';
import type { Shadows, Theme } from '@mui/material/styles';

export type ThemeMode = 'light' | 'dark';

type AppPalette = typeof colors.light | typeof colors.dark;

export const createAppTheme = (mode: ThemeMode, densityMode: DensityMode = 'standard'): Theme => {
  const palette: AppPalette = mode === 'light' ? colors.light : colors.dark;
  const primaryText = mode === 'dark' ? lighten(palette.primary, 0.2) : palette.primary;
  const successMain = palette.success;
  const successDark = darken(successMain, 0.2);
  const successLight = lighten(successMain, 0.2);
  const dangerMain = palette.danger;
  const dangerDark = darken(dangerMain, 0.2);
  const dangerLight = lighten(dangerMain, 0.2);
  const densityConfig = density[densityMode];
  const flatShadows = Array(25).fill('none') as unknown as Shadows;
  const opacityDisabled = opacity.disabled;
  const state: Theme['app']['state'] = {
    hover: alpha(palette.text, 0.06),
    active: alpha(palette.text, 0.12),
    focus: alpha(palette.primary, 0.24),
    disabled: alpha(palette.text, opacityDisabled),
    disabledBg: alpha(palette.text, 0.04),
  };

  return createTheme({
    palette: {
      mode,
      primary: { main: palette.primary, light: primaryText },
      success: { main: successMain, dark: successDark, light: successLight },
      warning: { main: palette.warning },
      error: {
        main: dangerMain,
        dark: dangerDark,
        light: dangerLight,
        contrastText: '#000000',
      },
      info: { main: palette.info },
      neutral: { main: palette.neutral },
      green: { main: palette.success },
      red: { main: palette.danger },
      orange: { main: palette.warning },
      brown: { main: palette.brown },
      blue: { main: palette.info },
      purple: { main: palette.purple },
      background: { default: palette.bg, paper: palette.surface },
      text: { primary: palette.text, secondary: palette.textMuted },
      divider: palette.divider,
      action: {
        hover: state.hover,
        selected: state.active,
        focus: state.focus,
        disabled: state.disabled,
        disabledBackground: state.disabledBg,
      },
    },
    breakpoints: { values: breakpoints },
    shape: { borderRadius: radii.md },
    typography: {
      fontFamily: typography.body.fontFamily,
      pageTitle: typography.pageTitle,
      sectionTitle: typography.sectionTitle,
      body: typography.body,
      bodyMuted: { ...typography.bodyMuted, color: palette.textMuted },
      label: typography.label,
      caption: typography.caption,
      mono: typography.mono,
    },
    shadows: flatShadows,
    components: {
      MuiTypography: {
        defaultProps: {
          variantMapping: {
            pageTitle: 'h1',
            sectionTitle: 'h2',
            body: 'p',
            bodyMuted: 'p',
            label: 'span',
            caption: 'span',
            mono: 'code',
          },
        },
      },
      MuiButtonBase: {
        styleOverrides: {
          root: { '&.Mui-focusVisible': { outline: `2px solid ${state.focus}`, outlineOffset: 2 } },
        },
        defaultProps: { disableRipple: true },
      },
      MuiButton: {
        defaultProps: { size: densityConfig.buttonSize, disableRipple: true },
        styleOverrides: {
          root: {
            transition: `background-color ${motion.durations.fast} ${motion.easing.standard}`,
            textTransform: 'none',
            '&:hover': { backgroundColor: 'transparent', boxShadow: 'none' },
          },
          contained: {
            '&:hover': { backgroundColor: palette.primary, boxShadow: 'none' },
            '&.Mui-disabled': { backgroundColor: state.disabledBg },
          },
          containedError: {
            backgroundColor: dangerDark,
            color: '#000000',
            '&:hover': { backgroundColor: dangerDark, boxShadow: 'none' },
            '&.Mui-disabled': { backgroundColor: state.disabledBg },
          },
          outlined: {
            color: primaryText,
            borderColor: alpha(primaryText, 0.4),
            '&:hover': { backgroundColor: 'transparent' },
          },
          text: { color: primaryText, '&:hover': { backgroundColor: 'transparent' } },
        },
      },
      MuiLink: {
        styleOverrides: { root: { color: primaryText } },
      },
      MuiIconButton: {
        defaultProps: { disableRipple: true },
      },
      MuiPaper: { defaultProps: { elevation: 0 } },
      MuiTextField: { defaultProps: { size: densityConfig.inputSize } },
      MuiSelect: { defaultProps: { size: densityConfig.inputSize } },
      MuiMenu: {
        styleOverrides: {
          list: { paddingTop: spacing.none, paddingBottom: spacing.none },
          paper: { borderRadius: radii.none },
        },
      },
      MuiPopover: { styleOverrides: { paper: { borderRadius: radii.none } } },
      MuiTablePagination: {
        styleOverrides: {
          actions: { '& .MuiIconButton-root:hover': { backgroundColor: 'transparent' } },
        },
      },
      MuiPickersDay: {
        styleOverrides: { root: { '&:hover': { backgroundColor: 'transparent' } } },
      },
      MuiPickersInputBase: {
        styleOverrides: {
          root: { '& .MuiIconButton-root:hover': { backgroundColor: 'transparent' } },
        },
      },
      MuiPickersArrowSwitcher: {
        styleOverrides: {
          root: { '& .MuiIconButton-root:hover': { backgroundColor: 'transparent' } },
        },
      },
      MuiPickersCalendarHeader: {
        styleOverrides: { switchViewButton: { '&:hover': { backgroundColor: 'transparent' } } },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            transition: `border-color ${motion.durations.fast} ${motion.easing.standard}`,
            '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: palette.primary },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderColor: palette.primary,
              borderWidth: 2,
            },
            '&.Mui-disabled': { backgroundColor: state.disabledBg, opacity: opacity.disabled },
          },
          input: {
            '&:not(.MuiInputBase-inputMultiline)': {
              boxSizing: 'border-box',
              paddingTop: spacing.none,
              paddingBottom: spacing.none,
              paddingLeft: spacing.lg,
              paddingRight: spacing.md,
            },
          },
        },
      },
      MuiCheckbox: { styleOverrides: { root: { '&:hover': { backgroundColor: 'transparent' } } } },
      MuiSwitch: {
        styleOverrides: {
          root: { '&:hover': { backgroundColor: 'transparent' } },
          switchBase: { '&:hover': { backgroundColor: 'transparent' } },
        },
      },
      MuiChip: { styleOverrides: { root: {} } },
      MuiDialog: {
        styleOverrides: {
          paper: { backgroundColor: palette.surface },
        },
      },
    },
    app: {
      colors: palette,
      radii,
      shadows: shadows[mode],
      spacing,
      breakpoints,
      sizes,
      zIndex,
      opacity,
      motion,
      containers,
      density: densityConfig,
      iconSizes,
      state,
    },
  });
};
